import { useMemo } from 'react';

import { useIncomingLinksData } from '../../store/incomingLinksBridge.ts';
import { useOutgoingLinks } from '../../store/outgoingLinksBridge.ts';
import { useRelatedContentData } from '../../store/relatedContentBridge.ts';
import {
	type LinksContextConnections,
	useLinksContextPrompt,
} from '../sections/useLinksContextPrompt.ts';

/**
 * The links prompt for the share-context menu's Rovo item, read off the link bridges rather
 * than taken as a prop, so the panel and graph modal use the same byline-owned links.
 *
 * Not `useConnectionSectionsData`: that also calls `usePagePeople`, firing an AGG query for data
 * the prompt never reads.
 *
 * Null when there is no url to send, so the item can hide itself.
 */
export const useShareContextLinksPrompt = (contentId?: string): string | null => {
	const { incomingLinks } = useIncomingLinksData(contentId);
	const { outgoingLinks } = useOutgoingLinks(contentId);
	const { relatedContent } = useRelatedContentData(contentId);

	// Same bridge-to-section mapping as `useConnectionSectionsData`, so the urls match the order
	// the panel lists them in.
	const connections: LinksContextConnections = useMemo(
		() => ({
			appearsIn: { links: incomingLinks.links },
			linksTo: { links: outgoingLinks },
			relatedContent: { links: relatedContent.links },
		}),
		[incomingLinks.links, outgoingLinks, relatedContent.links],
	);

	return useLinksContextPrompt(connections);
};
